import { Box, Flex } from '@chakra-ui/react';
import { ErrorMessage, Form, Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useMemo, useState } from 'react';

import {
  getNoheadersStatus,
  addStepTwoFormData,
  handleActionStepNext,
  getFormValues,
  getTableHeader,
  setSelectedAreas,
  clearStepperFormPropertyData,
  setNoheaders,
} from 'redux/DataImportSlice';
import { ActionSteptwoSchema } from 'validation/schemas/ActionStepSchema';
import { GetTableProperties } from 'redux/ItemPageSlice';
import { getBuilderDimensions } from 'redux/BuilderModeSlice';

import ActionStepFooter from '../ActionStepFooter';
import { FieldError, FieldLabel, StepCheckbox, StepFooterRow, StepSelect } from '../actionStepUi';

interface Properties {
  setHelpModalSeleceted: any;
  dataType: string;
}

const DimensionType = ({ dataType }: Properties) => {
  const dispatch: any = useDispatch();
  const [selectedDimId, setSelectedDimId] = useState<number | null>();
  const getFormValue = useSelector(getFormValues);
  const dimesnsionList = useSelector(getBuilderDimensions);
  const headerNames = useSelector(getTableHeader);
  const [, setSelectedDimensionDetails] = useState<any>(null);
  const [customHeaders, setCustomHeaders] = useState<any[]>(headerNames ?? []);
  const isHeader = useSelector(getNoheadersStatus);
  useEffect(() => {
    if (isHeader) {
      const array: string[] = new Array(26)
        .fill(1)
        .map((_: string, index: number) => String.fromCodePoint(65 + index));
      const newHeaders = array
        .slice(0, headerNames.length)
        .map((item: string, headerIndex: number) => {
          return { current: `${headerNames[headerIndex]}`, modified: `Column ${item}` };
        });
      setCustomHeaders(newHeaders);
    } else {
      setCustomHeaders(
        headerNames.map((item: string) => {
          return { current: `${item}`, modified: `${item}` };
        }),
      );
    }
  }, [isHeader, headerNames]);

  useMemo(() => {
    const calculateSelectedDimensionDetails = dimesnsionList?.find((item: any) => {
      if (selectedDimId && !getFormValue.dimension && item?.id === selectedDimId) {
        return item;
      }
      if (
        getFormValue?.dimension &&
        !selectedDimId &&
        item?.id === Number(getFormValue?.dimension)
      ) {
        return item;
      }
      if (getFormValue?.dimension && selectedDimId && item?.id === selectedDimId) {
        return item;
      }
      return null;
    });
    setSelectedDimensionDetails(calculateSelectedDimensionDetails);
  }, [selectedDimId, getFormValue.dimension]);

  useEffect(() => {
    if (getFormValue.column) {
      dispatch(setSelectedAreas({ selectedColumnOption: getFormValue.column }));
      dispatch(GetTableProperties({ selectedId: selectedDimId || getFormValue?.dimension }));
    }
  }, [getFormValue.column, selectedDimId]);
  const handleSubmit = (form: any, setSubmitting: (data: boolean) => void) => {
    dispatch(addStepTwoFormData(form));
    setSubmitting(false);
    dispatch(handleActionStepNext());
  };

  return (
    <Box w='100%' mt={5}>
      <Formik
        initialValues={{
          selectedDataType: '' || dataType || getFormValue.dataType,
          selecetedDimension: '' || getFormValue.dimension,
          selectedColumnOption: '' || getFormValue.column,
        }}
        validationSchema={ActionSteptwoSchema}
        onSubmit={(form, { setSubmitting }) => handleSubmit(form, setSubmitting)}
        enableReinitialize={true}
      >
        {({ values, errors, isSubmitting, setFieldValue }: any) => (
          <Form style={{ width: '100%' }}>
            <ErrorMessage name='selectedDataType' component={FieldError} />
            {values.selectedDataType === '1' && (
              <Flex direction='column' gap={4}>
                <Box>
                  <FieldLabel>Select the dimension you want to import to</FieldLabel>
                  <StepSelect
                    value={values.selecetedDimension}
                    placeholder='Choose dimension'
                    isClearable
                    isInvalid={Boolean(errors.selecetedDimension)}
                    isDisabled={isSubmitting}
                    options={(dimesnsionList ?? []).map((item: any) => ({
                      value: item?.id,
                      label: item?.name,
                    }))}
                    onChange={async (id) => {
                      await setFieldValue('selecetedDimension', id);
                      await setSelectedDimId(id === '' ? null : Number(id));
                      await dispatch(clearStepperFormPropertyData());
                    }}
                  />
                  <ErrorMessage name='selecetedDimension' component={FieldError} />
                </Box>

                {values.selecetedDimension && (
                  <>
                    <Box>
                      <FieldLabel>Select the column in the table with the item names</FieldLabel>
                      <StepSelect
                        value={values.selectedColumnOption}
                        placeholder='Choose column'
                        isClearable
                        isInvalid={Boolean(errors.selectedColumnOption)}
                        isDisabled={isSubmitting}
                        options={(headerNames ?? []).map((item: any, index: number) => ({
                          value: item,
                          label: customHeaders[index]?.modified ?? item,
                        }))}
                        onChange={async (role) => {
                          await setFieldValue('selectedColumnOption', role);
                          await dispatch(setSelectedAreas({ selectedColumnOption: role }));
                        }}
                      />
                      <ErrorMessage name='selectedColumnOption' component={FieldError} />
                    </Box>
                    <StepCheckbox
                      isChecked={Boolean(isHeader)}
                      onChange={() => dispatch(setNoheaders(!isHeader))}
                    >
                      File has no column headers
                    </StepCheckbox>
                  </>
                )}
              </Flex>
            )}

            <StepFooterRow>
              <ActionStepFooter
                nextButtonName='Continue'
                prevButtonName='Previous'
                isSubmitting={isSubmitting}
              />
            </StepFooterRow>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default DimensionType;
